import type { AuthId, CalendarId } from "@tento-lona/sheets";
import { EventPatch, type Option, RowNetworkKey } from "@tento-lona/sheets";
import { invariant, nonnull } from "@tento-core/assert";

import type { EventItem } from "../events/event-item.ts";
import type { Network } from "../network.ts";
import type { MutationController } from "@tento-lona/sheets/mutation";
import type {
  GestureRun,
  MutationDriver,
  TxStep,
} from "@tento-lona/sheets/mutation";
import { type MutationAck, MutationClientId } from "@tento-lona/sheets/mutation";

/**
 * The events binding of `MutationTransaction` for the recurrence FORK gestures —
 * "edit/move this occurrence" and "this and following". All four chains share one
 * shape: mask the master's recurrence (EXDATE / UNTIL-truncate) → create the
 * forked event → optionally patch the fork. Before this saga they ran as
 * unguarded `await` chains, so a mid-chain failure left an exdated master with
 * the occurrence gone (create failed) or a wrong-valued duplicate (patch failed),
 * with no rollback. `forkGesture` builds the chain as a {@link GestureRun} with
 * inverses — restore the original recurrence / delete the fork — and
 * `MutationTransaction.run` compensates the confirmed prefix on failure.
 *
 * Late binding lives in the op union, not the mechanism: the transaction never
 * inspects `Op`, so a step may target "the event a prior step created" through a
 * {@link OccurrenceSaga.CreatedSlot} thunk resolved at settle time. That is what
 * lets a pre-declared step list express the create→patch dependency.
 */
export namespace OccurrenceSaga {
  /**
   * Late-bound fork handle: filled by the driver when the create step settles,
   * read by the patch step's target thunk and by the create step's own inverse
   * (delete the fork). Reading before the create settles is a programming error
   * — the transaction settles steps strictly in order, and compensation only
   * runs an inverse for a step that already confirmed.
   */
  export class CreatedSlot {
    #item: Option<EventItem> = null;

    fill(item: EventItem): void {
      this.#item = item;
    }

    get(): EventItem {
      return nonnull(
        this.#item,
        "OccurrenceSaga.CreatedSlot: create step has not settled",
      );
    }
  }

  /**
   * One saga step. Targets are thunks so an op can reference an event that does
   * not exist at declaration time (the fork) — resolved when the step settles.
   */
  export type Op =
    | {
      readonly kind: "update";
      readonly target: () => EventItem;
      readonly patch: EventPatch;
    }
    | {
      readonly kind: "create";
      readonly authId: AuthId;
      readonly calendarId: CalendarId;
      readonly event: Network.NewEvent;
      /** The optimistic placeholder the create reconciles, when the
       *  gesture painted one. */
      readonly reconcile: Option<EventItem>;
      readonly into: CreatedSlot;
    }
    | { readonly kind: "delete"; readonly target: () => EventItem };

  /**
   * The client write surface the saga settles through — `EventsClient` hands
   * it its own `update` / `delete` and the canonical row's reconciling
   * `create`. Each method is itself optimistic + self-rolling-back (the
   * overlay projector drops its own op on failure), so a step is internally
   * atomic and the saga only has to compensate ACROSS steps.
   */
  export interface Writes {
    update(event: EventItem, patch: EventPatch): Promise<EventItem>;
    create(
      authId: AuthId,
      calendarId: CalendarId,
      event: Network.NewEvent,
      reconcile: Option<EventItem>,
    ): Promise<EventItem>;
    delete(event: EventItem): Promise<void>;
  }

  /** Inputs to {@link forkGesture} — the domain values the gesture site already
   *  computes (masked recurrence, fork payload); the builder owns step/inverse
   *  assembly. `patch` is the field edit applied to the fork after creation
   *  (update gestures) — null for moves, where the fork's range IS the edit. */
  export interface Fork {
    readonly label: string;
    readonly intent: string;
    readonly master: EventItem;
    /** The master's post-fork recurrence — EXDATE'd ("this") or
     *  UNTIL-truncated ("following"). */
    readonly maskedRecurrence: readonly string[];
    readonly forkEvent: Network.NewEvent;
    readonly patch: Option<EventPatch>;
    /** The fork's optimistic placeholder (painted before the gesture's
     *  first request), which the create step reconciles; `null` for none. */
    readonly placeholder: Option<EventItem>;
  }

  /**
   * Build the fork chain: mask master → create fork → (optionally) patch fork.
   * Inverses: restore the master's pre-gesture recurrence / delete the fork; the
   * final patch carries no inverse (nothing settles after it, so it is never
   * compensated).
   */
  export function forkGesture(args: Fork): GestureRun<Op> {
    const original = args.master.inner.recurrence;
    invariant(
      original != null && original.length > 0,
      `OccurrenceSaga.forkGesture: master ${args.master.id} has no recurrence`,
    );
    const created = new CreatedSlot();
    const steps: TxStep<Op>[] = [
      {
        op: {
          kind: "update",
          target: () => args.master,
          patch: EventPatch.of({ recurrence: [...args.maskedRecurrence] }),
        },
        inverse: {
          kind: "update",
          target: () => args.master,
          patch: EventPatch.of({ recurrence: [...original] }),
        },
      },
      {
        op: {
          kind: "create",
          authId: args.master.inner.authId,
          calendarId: args.master.inner.calendarId,
          event: args.forkEvent,
          reconcile: args.placeholder,
          into: created,
        },
        inverse: { kind: "delete", target: () => created.get() },
      },
    ];
    const patch = args.patch;
    if (patch != null) {
      steps.push({
        op: { kind: "update", target: () => created.get(), patch },
        inverse: null,
      });
    }
    return { label: args.label, intent: args.intent, steps };
  }
}

/**
 * {@link MutationDriver} over {@link OccurrenceSaga.Writes}. Each write is
 * already internally optimistic + self-rolling-back, so this driver runs the
 * transaction's degenerate mode: `beginOptimistic` is inert (the optimistic
 * effect happens inside `settle`, when the client write applies it), and
 * `drop` is a no-op — an abandoned (never-settled) step has touched neither
 * overlay nor server, and a failed settle already rolled its own optimism back
 * inside the write. `clientId` is a local correlation label only; it never
 * reaches an overlay (`EventOps` mints its own ids internally).
 */
export class OccurrenceSagaDriver
  implements MutationDriver<OccurrenceSaga.Op, EventItem> {
  #seq = 0;

  readonly #writes: OccurrenceSaga.Writes;
  constructor(writes: OccurrenceSaga.Writes) {
    this.#writes = writes;
  }

  beginOptimistic(
    _rowKey: RowNetworkKey,
    op: OccurrenceSaga.Op,
  ): MutationController.Handle<OccurrenceSaga.Op, EventItem> {
    const clientId = MutationClientId.of(`occ-${++this.#seq}`);
    return {
      op,
      settle: async (): Promise<MutationAck<EventItem>> => ({
        clientId,
        canonical: await this.#settleOp(op),
      }),
      drop: (): void => {},
    };
  }

  async perform(
    rowKey: RowNetworkKey,
    op: OccurrenceSaga.Op,
  ): Promise<MutationAck<EventItem>> {
    return this.beginOptimistic(rowKey, op).settle();
  }

  async #settleOp(op: OccurrenceSaga.Op): Promise<Option<EventItem>> {
    switch (op.kind) {
      case "update":
        return this.#writes.update(op.target(), op.patch);
      case "create": {
        const item = await this.#writes.create(
          op.authId,
          op.calendarId,
          op.event,
          op.reconcile,
        );
        op.into.fill(item);
        return item;
      }
      case "delete":
        await this.#writes.delete(op.target());
        return null;
    }
  }
}
